/**
 * Analytics Portal V1 — Capital directory discovery (Phase 13, plan §14 + Decision 3).
 *
 * The server sends the Capital directory as its own block with its own zero-state contract. When it
 * reports `zero_state: "supported_no_activity"` the approved copy is rendered verbatim — the metric
 * is supported and instrumented, there simply was no eligible activity, so it must never be
 * described as unavailable and never be omitted. Directory CTR is displayed only because the API
 * returns it as a rate object; it is never computed here.
 */
import React from "react";
import { KpiCard } from "./KpiCard";

const CARDS = [
  { key: "impressions", label: "Directory impressions" },
  { key: "clicks", label: "Directory result clicks" },
  { key: "unique_clickers", label: "Unique directory clickers" },
  { key: "ctr", label: "Directory click-through rate", suffix: "%" },
];

export const ZERO_STATE_HEADLINE =
  "No eligible Capital directory activity was recorded during this period.";
export const ZERO_STATE_BODY =
  "Impressions and clicks will appear here as eligible members discover this Capital profile "
  + "through the Capital directory.";

export const CapitalDirectoryCard = ({ directory, rangeSuggestion, onUseSupportedRange }) => {
  if (!directory) return null;
  const cards = CARDS.filter((card) => directory[card.key]);
  const noActivity = directory.zero_state === "supported_no_activity";

  return (
    <section className="space-y-3" data-testid="analytics-capital-directory"
             aria-labelledby="analytics-capital-directory-heading">
      <div>
        <h2 id="analytics-capital-directory-heading"
            className="text-base font-[620] text-foreground md:text-lg">Capital directory discovery</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          How eligible members find this profile in the Capital directory.
        </p>
      </div>

      {noActivity && (
        <div className="rounded-xl border border-border bg-secondary/40 px-4 py-3" role="note"
             data-testid="analytics-capital-directory-zero-state">
          <p className="text-sm font-[560] text-foreground">{ZERO_STATE_HEADLINE}</p>
          <p className="mt-0.5 text-sm text-muted-foreground">{ZERO_STATE_BODY}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <KpiCard key={card.key} metricKey={`directory_${card.key}`} label={card.label}
                   suffix={card.suffix || ""} metric={directory[card.key]}
                   rangeSuggestion={rangeSuggestion} onUseSupportedRange={onUseSupportedRange} />
        ))}
      </div>
    </section>
  );
};
